import { levelName } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import { pathStringFromMaterialSrcWithUserDefinedName } from "common/materials/pathStringFromMaterialSrc";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { groupRoomsOnLevel } from "doc/rooms/roomTraversal";
import { polyLine2Area } from "geom/polyLine2";
import { ascendingStringSortCompare } from "geom/utils";

export type RoomByFloorMaterialEntry = [string, string, string, string, DocEntityHandle];

/**
 * Sort rooms into rows by floor material
 */
export function selectRoomsByFloorMaterial(building: Building): {
  rows: { [key: string]: RoomByFloorMaterialEntry };
} {
  const roomsByRow: { [key: string]: RoomByFloorMaterialEntry } = {};

  const { rooms, walls, levels, roomOutlineTable, roomsByLevel, floorSystems } = building;

  type RoomFloorAndMatAreas = { [floorMat: string]: number };

  function getRoomFloorMatAreas(room: Room): RoomFloorAndMatAreas {
    const levelFloorSystem = floorSystems[room.level.id];
    if (!levelFloorSystem) {
      return {};
    }
    const roomFloorFinishes = levelFloorSystem.roomFloorFinishes[room.id];

    const floorOutlineInsideFraming = roomOutlineTable[room.id] && roomOutlineTable[room.id].INSIDE_OF_MASS_IGNORE_VOID;

    let floorOutlineArea = polyLine2Area(floorOutlineInsideFraming ?? []);
    const floorAndMaterialAreas: RoomFloorAndMatAreas = {};

    for (const finish of roomFloorFinishes) {
      let floorMat = "None";
      let floorMatArea = 0;

      const materialName = pathStringFromMaterialSrcWithUserDefinedName(finish.materialSrc);
      floorMat = materialName.length > 26 ? `${materialName.slice(0, 26) + "..."}` : materialName;

      const outerLoop = finish.finishFace.loops[0];
      const outerLoopArea = polyLine2Area(outerLoop);
      floorMatArea += outerLoopArea;

      for (let i = 1; i < finish.finishFace.loops.length; i++) {
        const innerLoopArea = polyLine2Area(finish.finishFace.loops[i]);
        floorMatArea -= innerLoopArea;
      }

      // subtract this area from total NONE area only if inside framing,
      // since the starting NONE area only represents the INSIDE_OF_FRAMING area
      if (!finish.isFloorTheshold) {
        floorOutlineArea -= floorMatArea;
      }

      if (!floorAndMaterialAreas[floorMat]) {
        floorAndMaterialAreas[floorMat] = 0;
      }
      floorAndMaterialAreas[floorMat] += floorMatArea;
    }

    floorAndMaterialAreas["None"] = floorOutlineArea;
    return floorAndMaterialAreas;
  }

  Object.keys(levels).forEach((levelId) => {
    const level = levels[levelId];
    const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
    const levelKey = levelName(level);

    // sort rooms loop through alphabelically
    const sortedRooms: Room[] = [];
    levelRooms.forEach((room) => {
      sortedRooms.push(room);
    });

    sortedRooms.sort((a, b) =>
      ascendingStringSortCompare(formatRoomName(a).toUpperCase(), formatRoomName(b).toUpperCase()),
    );

    const visitedRooms: { [roomId: string]: boolean } = {};

    sortedRooms.forEach((room) => {
      if (visitedRooms[room.id]) {
        return;
      }

      const roomSetFloorMatAreas: RoomFloorAndMatAreas = {};
      const roomPrettyName = formatRoomName(room);
      groupRoomsOnLevel(
        room,
        walls,
        (_, endRoom, wall) =>
          wall.wallType === WallType.VOID && roomPrettyName.toUpperCase() === formatRoomName(endRoom).toUpperCase(),
        (room) => {
          const roomFloorMatAreas = getRoomFloorMatAreas(room);
          for (const mat in roomFloorMatAreas) {
            if (!roomSetFloorMatAreas[mat]) {
              roomSetFloorMatAreas[mat] = 0;
            }
            roomSetFloorMatAreas[mat] += roomFloorMatAreas[mat];
          }
        },
        visitedRooms,
      );

      for (const roomSetMat in roomSetFloorMatAreas) {
        const roomSetMatArea = roomSetFloorMatAreas[roomSetMat];
        const roomSetMatHash = `${room.id}.${roomSetMat}`;
        const roundedTotalArea = Math.round((roomSetMatArea / 144) * 10) / 10;
        if (roundedTotalArea === 0) {
          continue;
        }
        roomsByRow[roomSetMatHash] = [
          roomPrettyName,
          `${roundedTotalArea} SF`,
          roomSetMat,
          levelKey ?? "",
          room.docEntityHandle,
        ];
      }
    });
  });

  return { rows: roomsByRow };
}

export function formatRoomName(room: Room): string {
  const roomTypeUpperCase = room.attributes.name.toUpperCase();
  const roomNameInPlan = room.nameInPlan;

  return roomNameInPlan?.trim() ? roomNameInPlan : roomTypeUpperCase;
}
